import React, { useEffect, useState } from 'react';
import { Animated, StyleProp, View, ViewStyle } from 'react-native';
import { Icon, IconName } from '@/components/Icon';
import { IconName as NeolexonIconName } from '@/components/NeolexonIcon/Icons/types';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { NewNavigationHighlight } from '@/components/NewNavigationHighlight';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';
import { SelectedNavItem } from '@/components/ui/therapist/CollapsibleSidebar/index';
import { Text } from '@/components/Text';
import { isNeolexonIcon } from '@/components/NeolexonIcon/Icons/';
import { useNavigationHighlight } from '@/hooks/useNewNavigationHighlight';
import { useOnHover } from '@/hooks/useOnHover';
import constants from '@/config/constants';

import { styles } from './styles';
import colors from '@/config/colors';

interface MenuItemProps {
  icon?: IconName | NeolexonIconName;
  label: string;
  labelOpacity?: Animated.AnimatedInterpolation<string | number>;
  labelTranslateX?: Animated.AnimatedInterpolation<string | number>;
  newNavigation?: boolean;
  onPress: () => void;
  outerStyle?: StyleProp<ViewStyle>;
  screensActive?: string[];
  showChevron?: boolean;
  selectedNavItem?: SelectedNavItem;
  testID: string;
}

export const MenuItem = ({
  icon,
  label,
  labelOpacity,
  labelTranslateX,
  newNavigation = false,
  onPress,
  outerStyle,
  screensActive,
  showChevron,
  selectedNavItem,
  testID,
}: MenuItemProps) => {
  const enabled = constants.appVersion === '1.10.29' && newNavigation;
  const sessionKey = 'newNavAnimationCountNav';
  const { onClickIncrement, shouldHighlight } = useNavigationHighlight({
    sessionKey,
    maxCount: 5,
    enabled,
  });
  const [localIsSelected, setLocalIsSelected] = useState<boolean>(false);
  const { appTitle } = useSidebarAnimation();
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover();

  useEffect(() => {
    setLocalIsSelected(
      screensActive ? screensActive.includes(appTitle) : false,
    );
  }, [appTitle]);

  useEffect(() => {
    setLocalIsSelected(selectedNavItem === label);
  }, [selectedNavItem]);

  useEffect(() => {
    const isRouteActive = screensActive
      ? screensActive.includes(appTitle)
      : false;
    const isExpanded = selectedNavItem === label;

    setLocalIsSelected(isExpanded || isRouteActive);
  }, [appTitle, selectedNavItem]);

  return (
    <PressableNeolexon
      onPress={() => {
        onClickIncrement();
        onPress();
      }}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      style={outerStyle}
      testID={testID}
    >
      <View
        style={[
          styles.menuItemContainer,
          localIsSelected && {
            backgroundColor: colors.newUI.therapist.bgBlue10,
            borderLeftWidth: 8,
            borderColor: colors.newUI.therapist.green70,
            paddingLeft: 0,
          },
          isHovered && { opacity: 0.5 },
        ]}
      >
        {isNeolexonIcon(icon) ? (
          <NeolexonIcon
            name={icon}
            size={28}
            color={colors.newUI.therapist.primaryBlue80}
            style={{ paddingLeft: 4 }}
            fillColor={
              localIsSelected ? colors.newUI.therapist.primaryBlue80 : 'none'
            }
          />
        ) : (
          <Icon
            name={icon as IconName}
            size={28}
            color={colors.newUI.therapist.primaryBlue80}
            style={{ paddingLeft: 4 }}
          />
        )}
        <Animated.View
          style={[
            styles.expandedMenuItemWrapper,
            labelOpacity &&
              labelTranslateX && {
                opacity: labelOpacity,
                transform: [{ translateX: labelTranslateX }],
              },
          ]}
        >
          <NewNavigationHighlight enabled={enabled ? shouldHighlight : null}>
            <Text style={{ paddingLeft: 15, fontWeight: 600 }}>{label}</Text>
          </NewNavigationHighlight>
          {showChevron ? (
            <Icon
              name={'chevronRight'}
              size={20}
              color={colors.newUI.therapist.primaryBlue80}
              style={{ paddingRight: 40 }}
            />
          ) : null}
        </Animated.View>
      </View>
    </PressableNeolexon>
  );
};
